import {
    createColumnHelper,
    Row,
    SortingState,
    Table,
} from '@atbay/cyber-components'
import { useDrawer } from '@core/context/DrawerContext'
import { TServiceRequest } from '@core/modules/mdr/cases'
import { formatDate } from '@core/utils/time'
import { FC, useState } from 'react'
import { useBreakpoints } from '@/core/hooks/useBreakpoints/useBreakpoints'
import { Cell, HeaderText } from './ServiceRequestsTable.styles'
import { StyledCustomTable } from '@components/CustomTable/CustomTable.styles'
import { TStatus } from '../cases-table/CasesTable.utils'
import TableEmpty from '../table/table-empty'
import { HeaderStatusBadge } from '@/ui/components/HeaderStatusBadge'
import Typography from '../typography'
import CaseNumberDisplay from '@/ui/components/caseNumberDisplay'
import { getTableRowContext } from '@/ui/utils/tableUtils'
import { AccountModule } from '@/core/modules/account/account.module'
import { Analytics } from '@core/services/mixpanel'

interface CasesTableProps {
    cases: TServiceRequest[]
}

const ServiceRequestsTable: FC<CasesTableProps> = ({ cases }) => {
    const [sorting, setSorting] = useState<SortingState>([])
    const { setDrawerState, drawerState } = useDrawer()
    const { belowBreakpoint } = useBreakpoints()
    const { isMdrActive } = AccountModule.useStore()
    const {
        isDrawerOpen,
        contentProps: { cases: sortedCases = [], currentIndex: sortedIndex },
    } = drawerState

    const activeCaseNumber = isDrawerOpen
        ? sortedCases?.[sortedIndex]?.caseNumber
        : undefined

    const openDrawer = (
        row: Row<TServiceRequest>,
        table: Table<TServiceRequest>
    ) => {
        Analytics.event('SEM::mdr_service_request_details_drawer_open', {
            isMdrActive,
            origin: 'mdr_service_requests_table',
        })

        const { sortedIndex, items: sortedCases } = getTableRowContext(
            row,
            table
        )

        setDrawerState((prevState: any) => ({
            ...prevState,
            isDrawerOpen: true,
            drawerContentType: 'serviceRequestDetails',
            contentProps: {
                width: belowBreakpoint('xl')
                    ? '73vw'
                    : belowBreakpoint('xxl')
                      ? '68vw'
                      : '55vw',
                numberOfItems: sortedCases.length,
                currentIndex: sortedIndex,
                cases: sortedCases,
                withNavigation: true,
            },
        }))
    }

    const columnHelper = createColumnHelper<TServiceRequest>()

    const columns = [
        {
            ...columnHelper.accessor('title', {
                cell: (info) => (
                    <Cell
                        data-testid="subject"
                        onClick={() => openDrawer(info.row, info.table)}
                    >
                        <Typography className="table-cell-text">
                            {info.cell.getValue() || ''}
                        </Typography>
                    </Cell>
                ),
                header: () => (
                    <HeaderText
                        variant="xs-regular"
                        className="table-cell-text"
                    >
                        Subject
                    </HeaderText>
                ),
            }),
            width: '35%',
        },
        {
            id: 'caseNumber',
            header: () => (
                <HeaderText variant="xs-regular" className="table-cell-text">
                    Case Number
                </HeaderText>
            ),
            cell: (info: any) => {
                const caseNumber = info.row.original.caseNumber as string
                return (
                    <Cell cursor="text" className="table-cell-text">
                        <CaseNumberDisplay caseNumber={caseNumber} />
                    </Cell>
                )
            },
        },
        columnHelper.accessor('createdAt', {
            cell: (info) => (
                <Cell
                    data-testid="created-date"
                    onClick={() => openDrawer(info.row, info.table)}
                >
                    <Typography className="table-cell-text">
                        {formatDate(info.cell.getValue())}
                    </Typography>
                </Cell>
            ),
            header: () => (
                <HeaderText variant="xs-regular" className="table-cell-text">
                    Created Date
                </HeaderText>
            ),
            enableSorting: true,
        }),
        columnHelper.accessor('status', {
            cell: (info) => (
                <Cell
                    data-testid="status"
                    onClick={() => openDrawer(info.row, info.table)}
                    className="table-cell-text"
                >
                    <HeaderStatusBadge
                        status={info.cell.getValue() as TStatus}
                        variant="serviceRequest"
                        className="table-cell-text"
                    />
                </Cell>
            ),
            header: () => <HeaderText variant="xs-regular">Status</HeaderText>,
            enableSorting: true,
        }),
    ]
    return (
        <StyledCustomTable
            activeRow={activeCaseNumber}
            getRowId={(row: TServiceRequest) => row.caseNumber}
            columns={columns}
            data={cases}
            sorting={sorting}
            setSorting={setSorting}
            emptyState={<TableEmpty message="No service requests found" />}
        />
    )
}

export default ServiceRequestsTable
